import { DocumentNode } from 'graphql';
import { uniq } from 'lodash';
import { graphql, OptionProps } from 'react-apollo';
import configureApolloClient from '../../apollo/configureApolloClient';
import { ActivityChartQuery, ActivityMapQuery } from './queries';
import {
    Activity,
    ActivityChartContainerProps,
    ActivityMapContainerProps,
    ActivityMapOrChartPayload,
    ActivityMapOrChartVariables,
    ActivityPayload,
    ActivityStreamInput,
    ActivityStreamProps,
    CustomStreamType,
    ScalarStream,
    StravaResolution,
    StravaStreamType
} from './types';

export const DEFAULT_RESOLUTION = StravaResolution.medium;

/** Prefetch activity streams */
export async function fetchActivityStreams(props: ActivityStreamInput) {
    const client = configureApolloClient();
    const { types, selectedStreamType, id, resolution } = props;

    if (!types) {
        throw new Error('No stream types specified');
    }

    const xAxisTypes = [StravaStreamType.distance, StravaStreamType.time];

    const map = client.query({
        query: ActivityMapQuery,
        variables: {
            input: {
                id: id.toString()
            },
            resolution: resolution || DEFAULT_RESOLUTION,
            streamTypes: selectedStreamType
                ? uniq([selectedStreamType, ...xAxisTypes]).sort()
                : xAxisTypes
        }
    });

    const charts = types
        .filter(t => xAxisTypes.indexOf(t as StravaStreamType) === -1)
        .map(async t => {
            await client.query({
                query: ActivityChartQuery,
                variables: {
                    input: {
                        id: id.toString()
                    },
                    resolution: resolution || DEFAULT_RESOLUTION,
                    streamTypes: uniq([t, ...xAxisTypes]).sort()
                }
            });
        });

    await Promise.all<any>([map, ...charts]);
}

/** Prefretch scalar streams */
export async function fetchScalarStreams({
    id,
    resolution,
    streamTypes
}: {
    id: string;
    resolution: StravaResolution;
    streamTypes: (StravaStreamType | CustomStreamType)[];
}) {
    const client = configureApolloClient();

    return client.query<{
        activity: { activity: { scalarStreams: ScalarStream[] } };
    }>({
        query: ActivityChartQuery,
        variables: {
            input: { id },
            resolution,
            streamTypes: streamTypes.slice().sort()
        }
    });
}

/**
 * Provides a HOC to inject map streams, given a query and query name.
 * Used for main charts and demo charts.
 */
export const withMapOrChartStreamsCore = <TProps extends {}>(
    query: DocumentNode,
    queryName: string = 'activity'
) => {
    return graphql<
        TProps & ActivityStreamProps,
        ActivityMapOrChartPayload,
        ActivityMapOrChartVariables,
        TProps & { activity?: Activity; error?: any; loading?: boolean }
    >(query, {
        options: (props: ActivityStreamProps) => {
            const { id } = props;
            if (!id) {
                throw new Error('No id specified');
            }
            return {
                variables: {
                    input: { id },
                    resolution: props.resolution,
                    streamTypes: uniq([
                        ...props.xAxisStreamTypes,
                        ...(props.yAxisStreamType
                            ? [props.yAxisStreamType]
                            : []),
                        StravaStreamType.distance,
                        StravaStreamType.time
                    ]).sort() as (StravaStreamType | CustomStreamType)[]
                }
            };
        },
        props: (
            props: OptionProps<ActivityStreamProps & TProps, any>
        ): TProps & { activity?: Activity; error?: any; loading?: boolean } => {
            const { data, ownProps } = props;

            if (!data) {
                throw new Error('No data provided');
            }

            const { error, loading } = data;
            const payload: ActivityPayload = data[queryName];

            return {
                ...(ownProps as any),
                activity: payload ? payload.activity : undefined,
                error,
                loading
            };
        }
    });
};

/** HOC which injects map streams */
export const withMapStreams = withMapOrChartStreamsCore<
    ActivityMapContainerProps
>(ActivityMapQuery);

/** HOC which injects chart streams */
export const withChartStreams = withMapOrChartStreamsCore<
    ActivityChartContainerProps
>(ActivityChartQuery);



// WEBPACK FOOTER //
// ./src/client/components/Strava/withActivityStreams.tsx